import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { Button } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { UpdateLogoProgress } from '@root/widgets/premium/update-logo-drawer/ui/common/update-logo-progress'

import { reatomUpdateBannerDrawer } from '../../atoms'

import { PremiumBannerDrawerPreview } from './update-banner-preview'

import type { PairModel } from '@root/entities/pair'

type Props = {
  pair: PairModel
  open: boolean
  onClose: () => void
}

export const UploadPremiumBannerContent: React.FC<Props> = ({
  pair,
  open,
  onClose,
}) => {
  const bannerInputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomUpdateBannerDrawer({
        premiumMeta: pair.premiumInfo,
      }),
    [pair.premiumInfo],
  )
  const [requestState] = useAtom(model.updateRequestState)
  const [bannerPreview] = useAtom(model.bannerPreviewAtom)
  const [banner] = useAtom(model.bannerAtom)
  const [currentBanner] = useAtom(pair.premiumInfo.banner)

  const uploadBanner = useAction(model.updateBannerAction)
  const setBanner = useAction(model.setBannerAction)
  const clearBanner = useAction(model.clearBannerAction)
  const reset = useAction(model.resetAction)

  const handleUploadBanner = () =>
    bannerInputRef.current?.dispatchEvent(
      new MouseEvent('click', { bubbles: true }),
    )

  const clearFileInput = () => {
    if (bannerInputRef.current) {
      bannerInputRef.current.value = ''
    }
  }
  const handleReset = () => {
    reset()
    clearFileInput()
  }
  const handleClose = () => {
    handleReset()
    onClose()
  }
  const handleRevertBanner = () => {
    clearBanner()
    clearFileInput()
  }

  useEffect(() => {
    if (!open) {
      handleReset()
    }
    // Reset only when the drawer closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Root>
      <UpdateLogoProgress
        requestState={requestState}
        onClose={handleClose}
        onReset={handleReset}
      />
      <PremiumBannerDrawerPreview
        pair={pair}
        onUploadClick={handleUploadBanner}
        onRevertClick={bannerPreview ? handleRevertBanner : undefined}
        imagePreview={bannerPreview || currentBanner}
      />
      <Input
        ref={bannerInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) {
            setBanner(file)
          }
        }}
      />
      <Action>
        <ButtonBack
          radius="rounded"
          variant="secondary"
          type="button"
          size="m"
          onClick={handleClose}
        >
          {t('common.back')}
        </ButtonBack>
        <Button
          type="button"
          radius="rounded"
          variant="solid"
          color="primary"
          size="m"
          disabled={!banner}
          onClick={uploadBanner}
        >
          {t('common.process')}
        </Button>
      </Action>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  text-align: center;
`

const Action = styled.footer`
  display: grid;
  grid-template-columns: 1fr 1fr;

  gap: 10px;
  padding: 42px 12px 34px;
`

const ButtonBack = styled(Button)`
  color: var(--text-11);
`

const Input = styled.input`
  display: none;
`
